<script>
import Layout from "@/router/layouts/auth";
import { api } from "@/state/services";
import { mapState } from "vuex";
import { required, email } from "vuelidate/lib/validators";
import Languages from "@/components/widgets/languages";

export default {
  locales: {
    pt: {
      'Forgot your password?': 'Esqueceu sua senha?',
      'Username': 'Usuário',
      'Username is required.': 'O nome de usuário é obrigatório.',
      'E-mail': 'E-mail',
      'E-mail is required.': 'O e-mail é obrigatório.',
      'This value should be a valid e-mail.': 'Este valor deve ser um e-mail válido.',
      'The email you entered is incorrect.': 'O e-mail informado está incorreto.',
      'Recover': 'Recuperar',
      'Remember It?': 'Lembrou-se?',
      'Sign In here': 'Entrar',
      'The username or email is invalid.': 'O usuário ou email é inválido.',
      'TECHNOLOGY BY': 'TECNOLOGIA POR',
      'A code for creating a new password has been sent to your email and you have up to 24 hours to change your password.': 'Um código para criar uma nova senha foi enviado ao seu e-mail e você tem até 24 horas para alterar sua senha.',
    },
    es: {
      'Forgot your password?': '¿Olvidaste tu contraseña?',
      'Username': 'Nombre de Usuario',
      'Username is required.': 'El nombre de usuario es obligatorio.',
      'E-mail': 'Correo Electrónico',
      'E-mail is required.': 'El correo electrónico es obligatorio.',
      'This value should be a valid e-mail.': 'Este valor debe ser un correo electrónico válido.',
      'The email you entered is incorrect.': 'El correo electrónico ingresado es incorrecto.',
      'Recover': 'Recuperar',
      'Remember It?': '¿Ya la recordaste?',
      'Sign In here': 'Iniciar sesión',
      'The username or email is invalid.': 'El nombre de usuario o correo electrónico no es válido.',
      'TECHNOLOGY BY': 'TECNOLOGÍA POR',
      'A code for creating a new password has been sent to your email and you have up to 24 hours to change your password.': 'Se ha enviado un código para crear una nueva contraseña a tu correo electrónico y tienes hasta 24 horas para cambiar tu contraseña.',
    }
  },
  components: {
    Layout,
    Languages
  },
  data() {
    return {
      loading: false,
      success: false,
      username: "",
      email: ""
    };
  },
  computed: {
    ...mapState({
      alert: state => state.alert
    })
  },
  validations: {
    username: { required },
    email: { required, email },
  },
  methods: {
    recoverSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      } else {
        this.loading = true

        if (this.username && this.email) {
          api
            .post('password/recover', {
              username: this.username,
              email: this.email,
            })
            .then(response => {
              if (response.data.status == 'success') {
                this.username = ''
                this.email = ''
                this.$v.$reset()

                this.alert.type = 'alert-success'
                this.alert.message = this.t(response.data.message)

                this.success = true
              } else {
                this.alert.type = 'alert-danger'
                this.alert.message = this.t(response.data.message)
              }

              this.loading = false
            })
        }
      }
    }
  }
};
</script>

<template>
  <Layout>
    <div id="auth" class="auth-login d-flex">
      <div class="login-hero d-lg-none">
        <router-link class="login-hero-logo" tag="a" to="/">
          <img src="@/assets/images/logo-white.png" alt="Nexteev" />
        </router-link>
        <svg class="login-wave" viewBox="0 0 375 60" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,30 C120,58 260,6 375,30 L375,60 L0,60 Z" />
        </svg>
      </div>
      <div class="login-content">
        <div class="pt-5 pb-2 text-center d-none d-lg-block">
          <router-link class="align-middle" tag="a" to="/">
            <img class="w-100" style="max-width: 250px" src="@/assets/images/logo.png" />
          </router-link>
        </div>
        <div class="py-5 login-form">
          <h5 class="mb-4">{{ t('Forgot your password?') }}</h5>

          <div v-if="success">
            {{ this.alert.message }}
          </div>
          <b-form v-else @submit.prevent="recoverSubmit">
            <div v-if="alert.message" :class="'alert ' + alert.type">
              {{ alert.message }}
            </div>
            <b-form-group id="username" :label="t('Username')" label-for="username">
              <b-form-input id="username" v-model="username" type="text" :class="{ 'is-invalid': $v.username.$error }" v-mask="'XXXXXXXXXXXXXXXXXXXXXXXXXXXXX'"></b-form-input>
              <div v-if="$v.username.$error" class="invalid-feedback">
                <span v-if="!$v.username.required">{{ t('Username is required.') }}</span>
              </div>
            </b-form-group>
            <b-form-group id="email" :label="t('E-mail')" label-for="email">
              <b-form-input id="email" v-model="email" type="email" :class="{ 'is-invalid': $v.email.$error }"></b-form-input>
              <div v-if="$v.email.$error" class="invalid-feedback">
                <span v-if="!$v.email.required">{{ t('E-mail is required.') }}</span>
                <span v-if="!$v.email.email">{{ t('The email you entered is incorrect.') }}</span>
              </div>
            </b-form-group>
            <div class="mt-4">
              <b-button :disabled="loading == true" type="submit" variant="default" class="btn-lg btn-block">
                {{ t('Recover') }}
                <b-spinner v-if="loading" small class="ml-2 align-middle" variant="secondary" role="status"></b-spinner>
              </b-button>
            </div>
          </b-form>
          <div class="mt-4 text-center">
            <p class="text-dark">
              {{ t('Remember It?') }}
              <router-link tag="a" to="/login" class="btn btn-outline-default btn-lg btn-block">{{ t('Sign In here') }}</router-link>
            </p>
            <Languages type="dropup"></Languages>
            <p class="mt-5 font-size-12 text-uppercase text-muted">
              <a class="text-muted font-size-11" target="_blank" href="https://m2n.com.br/?utm_source=nexteev.com&utm_medium=link&utm_campaign=logo">
                {{ t('TECHNOLOGY BY') }}<br />
                <img alt="M2N" style="width: 50px" src="@/assets/images/logo-m2n.svg" />
              </a>
            </p>
          </div>
        </div>
      </div>
      <div class="flex-fill login-image d-none d-lg-block"></div>
    </div>
  </Layout>
</template>
